import { COLORS } from './chartColors';
import { StatusFilter } from '../../hooks/useChartData';
import { sortLabelsChronologically } from './helpers/dateHelpers';
import { parseDataKey } from './helpers/dataHelpers';
import { groupByPeriodAndKey } from './helpers/dataGroupingHelpers';
import { SelectedEntity, JobData, JobRecord } from './helpers/types';

// Utility function to convert snake_case to title case
const formatStateName = (stateName: string): string => {
  return stateName
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

export const jobCountStateTransformer = (
  data: Record<string, JobData>,
  selectedEntities: SelectedEntity[],
  selectedStates: StatusFilter[] = []
) => {
  // Flatten all records and attach period label from key
  const allRecords: (JobRecord & { _periodLabel: string })[] = [];
  Object.entries(data).forEach(([key, value]) => {
    const { monthLabel, shouldInclude } = parseDataKey(key, selectedEntities);
    if (!shouldInclude) return;
    if (Array.isArray(value.data)) {
      value.data.forEach((record) => {
        allRecords.push({ ...record, _periodLabel: monthLabel });
      });
    }
  });

  // Filter by selected states if any are selected
  const isAllStatesSelected = selectedStates?.some(
    (stateFilter: StatusFilter) => stateFilter.name === 'All'
  );
  const filteredRecords =
    selectedStates && selectedStates.length > 0 && !isAllStatesSelected
      ? allRecords.filter((record) =>
          selectedStates.some(
            (stateFilter: StatusFilter) =>
              stateFilter.name === formatStateName(record.state || 'Unknown')
          )
        )
      : allRecords;

  // Group by period label and state
  const monthlyStateCounts = groupByPeriodAndKey(
    filteredRecords,
    (record) => record._periodLabel,
    (record) => record.state || 'Unknown'
  );

  const allStates = new Set<string>(filteredRecords.map((r) => r.state || 'Unknown'));
  const sortedLabels = sortLabelsChronologically(Object.keys(monthlyStateCounts));

  const datasets = Array.from(allStates).map((state, index) => ({
    label: formatStateName(state),
    data: sortedLabels.map((label) => monthlyStateCounts[label][state] || 0),
    backgroundColor: COLORS[index % COLORS.length],
    borderWidth: 1,
    stack: 'jobStates'
  }));

  return {
    labels: sortedLabels,
    datasets
  };
};
